// react
import React, { useRef, useState } from 'react'

// external libs
import path from 'path'
import { FieldValidator } from 'final-form'
import { useForm, useField } from 'react-final-form'

// hooks
import useAppAlertMessageAction from '../../../../../stores/appAlertMessage/appAlertMessageAction'
import useTranslate from '../../../../../i18n/useTranslate'
import useWebService from '../../../../../services/web/useWebService'

// utils & constants
import { composeValidators, uploadRequired } from '../../../../../utilities/validate'
import { fileExtUploadFilelist } from '../../../../../constants/constants'
import { requestWebServiceTypes } from '../../../../../services/web/types'

// types
import type {
    MyFileType,
    NewReturnListFileType,
    ReturnDataType,
    SubTopicPageList,
    UploadResponseType,
    UseUploadFileType,
} from './uploadFileType'

const useUploadFile = (props: UseUploadFileType) => {
    const {
        required,
        requiredErrorMessage,
        validates = [],
        initialData,
        topicID,
        name,
        disabled,
        resetLstFileUpload,
    } = props
    const appAlertMessageAction = useAppAlertMessageAction()
    const callWebService = useWebService()
    const translate = useTranslate()
    const refElementUpload = useRef<any>()
    const refElementUploadEdit = useRef<any>()
    const refGroupFileIndex = useRef<number>() //index ของไฟล์ที่อยู่ภายในหัวข้อนั้น ๆ
    const refElementTextField = useRef<any>()
    const refDialog = useRef<Record<string, Function>>()
    const refDialogDownload = useRef<Record<string, Function>>()
    const [lstFileUpload, setLstFileUpload] = useState<NewReturnListFileType[]>()
    const [lstFileChange, setLstFileChange] = useState<any>([])
    const [lstFileHistory, setLstFileHistory] = useState<any>([])
    const [returnData, setReturnData] = useState<ReturnDataType>()
    const form = useForm()

    const { values, dirty } = form.getState()
    const { IRB } = values

    React.useEffect(() => {
        setLstFileUpload([])
        if (initialData) {
            initialData?.forEach((upload: any) => {
                if (upload.uploadFileObj.documentTopicID === topicID) {
                    if (upload.uploadFileObj.fileList.length > 0) {
                        let listFile = upload.uploadFileObj.fileList.map((item: any) => {
                            return {
                                ...item,
                                documentTopicID: topicID,
                            }
                        })
                        setLstFileUpload(listFile)
                    }
                    setReturnData({ ...upload.uploadFileObj })
                    field.input.onChange({ ...upload.uploadFileObj })
                }
            })
        } else {
            setLstFileUpload([])
            setReturnData(undefined)
        }
    }, [JSON.stringify(initialData), topicID])

    React.useEffect(() => {
        if (!disabled) {
            //clear file when IRB Change
            if (lstFileUpload && lstFileUpload?.length > 0 && dirty) {
                setLstFileUpload([])
                setReturnData(undefined)
            }
        }
    }, [IRB?.value])

    React.useEffect(() => {
        setLstFileUpload([]) //clear data first
        if (initialData) {
            initialData?.forEach((upload: any) => {
                if (upload.uploadFileObj.documentTopicID === topicID) {
                    if (upload.uploadFileObj.fileList.length > 0) {
                        let listFile = upload.uploadFileObj.fileList.map((item: any) => {
                            return {
                                ...item,
                                documentTopicID: topicID,
                            }
                        })
                        setLstFileUpload(listFile)
                    }
                    setReturnData({ ...upload.uploadFileObj })
                    field.input.onChange({ ...upload.uploadFileObj })
                }
            })
        } else {
            setLstFileUpload([])
            setReturnData(undefined)
        }
    }, [resetLstFileUpload])

    const mergeValidate = () => {
        let validate: Array<FieldValidator<any>> = []
        if (typeof required === 'boolean' && required) {
            validate = [uploadRequired(requiredErrorMessage), ...validates]
        }

        return validate
    }

    const field = useField<string>(name, {
        validate: composeValidators(...mergeValidate()),
    })

    const handleClickUpload = () => {
        refElementUpload.current.click()
    }
    const checkDuplicateFile = (lstFile: Array<MyFileType>) => {
        const listFileName = lstFile.map((file) => {
            return file.name
        })

        const duplicateFile = lstFileUpload?.filter(function (oldFile) {
            return listFileName.indexOf(oldFile.filename) > -1
        })

        if (duplicateFile && duplicateFile?.length > 0) {
            return true
        } else {
            return false
        }
    }

    const checkFileExtension = (event: any, accept: string) => {
        const name = event.target.files[0].name
        const listExtension = accept.split(',')

        for (var j = 0; j < listExtension.length; j++) {
            var sCurExtension = listExtension[j]
            if (
                name.substring(name.length - sCurExtension.length, name.length).toLowerCase() ===
                sCurExtension.toLowerCase()
            ) {
                return true
            }
        }
        return false
    }

    const handleClickChange = (index: any) => {
        refElementUploadEdit.current.click()
        refGroupFileIndex.current = index
    }

    const handleChangeUploadEdit = async (event: any, mainTopicID: number) => {
        const fileList: FileList = event.target.files
        let lstFile: Array<MyFileType> = []
        let lstDisplayName: Array<string> = []
        const numFiles = fileList.length
        const numMaxUploadFile = Number(process.env.REACT_APP_MAX_UPLOAD_FILE_DOCUMENT)

        if (numFiles > 0) {
            for (let i = 0; i < numFiles; i++) {
                const file: MyFileType = fileList[i]
                if (fileList[i].size > 31457280 || numFiles > numMaxUploadFile) {
                    //30 mb to byte (1 mb = 1048576 byte)
                    appAlertMessageAction.openAlertMessage({
                        title: translate.get('ALERT.TITLE.WARNING'),
                        message: translate.get('ALERT.CONTENT.UPLOAD_NOT_EXCEED_FILE', [String(numMaxUploadFile)]),
                    })

                    lstFile = []
                    lstDisplayName = []

                    break
                }
                const fileExt = path.extname(`${fileList[i].name}`)
                if (fileExtUploadFilelist.indexOf(fileExt) === -1) {
                    appAlertMessageAction.openAlertMessage({
                        title: translate.get('ALERT.TITLE.WARNING'),
                        message: translate.get('LABEL.UPLOAD_NOT_EXCEED_30_MB'),
                    })

                    lstFile = []
                    lstDisplayName = []

                    break
                }
                lstFile.push(file)
                lstDisplayName.push(file.name)
            }
        }

        const uploadRes = await callUploadFile(lstFile, mainTopicID)
        let myNewListFile: NewReturnListFileType[]
        myNewListFile = uploadRes.map((itm: UploadResponseType) => {
            return {
                no: null,
                date: new Date(),
                fileToken: `${itm.fileToken}`,
                filename: itm.filename,
                path: itm.path,
                isNewFile: true,
                documentTopicID: mainTopicID,
            }
        })

        if (myNewListFile && myNewListFile.length > 0) {
            let newLstFile = JSON.parse(JSON.stringify(lstFileUpload))

            if (lstFileUpload) {
                const fileGroupIndex = refGroupFileIndex.current as number
                setLstFileChange([...lstFileChange, newLstFile[fileGroupIndex]])

                myNewListFile[0].replaceFileID = newLstFile[fileGroupIndex].fileID

                newLstFile[fileGroupIndex] = myNewListFile[0]
            }

            //@ts-ignore
            setLstFileUpload(newLstFile) // fordisplay
            //@ts-ignore
            let newReturnData = { ...returnData, fileList: newLstFile }
            if (newLstFile.length === 0) {
                newReturnData = { ...returnData, fileList: newLstFile, subTopicPageList: [] } //กรณีลบไฟล์ออกหมดจะให้เคลียร์ค่า page ออกด้วย
            } else {
                newReturnData = { ...returnData, fileList: newLstFile }
            }
            setReturnData(newReturnData)
            field.input.onChange(newReturnData)
            setLstFileUpload(newLstFile)
            refElementTextField.current.value = newLstFile?.join(', ')
        }
    }

    const handleChangeUpload = async (event: any, mainTopicID: number, accept: string) => {
        const fileList: FileList = event.target.files
        let lstFile: Array<MyFileType> = []
        let lstDisplayName: Array<string> = []
        const currentListFileNum = lstFileUpload ? lstFileUpload.length : 0
        const numFiles = fileList.length
        const numMaxUploadFile = Number(process.env.REACT_APP_MAX_UPLOAD_FILE_DOCUMENT)
        if (numFiles > 0) {
            const checkExtension = checkFileExtension(event, accept)
            if (!checkExtension) {
                appAlertMessageAction.openAlertMessage({
                    title: translate.get('ALERT.TITLE.WARNING'),
                    message: translate.get('ALERT.CONTENT.UPLOAD_INVALID_FILE_EXTENSION'),
                })
            }
            if (checkExtension) {
                for (let i = 0; i < numFiles; i++) {
                    const file: MyFileType = fileList[i]
                    file.fileID = `${i}_${fileList[i].lastModified}_${fileList[i].name}`
                    if (fileList[i].size > 31457280 || numFiles + currentListFileNum > numMaxUploadFile) {
                        //10 mb to byte (1 mb = 1048576 byte)
                        appAlertMessageAction.openAlertMessage({
                            title: translate.get('ALERT.TITLE.WARNING'),
                            message: translate.get('ALERT.CONTENT.UPLOAD_NOT_EXCEED_FILE', [String(numMaxUploadFile)]),
                        })

                        lstFile = []
                        lstDisplayName = []

                        break
                    }
                    const fileExt = path.extname(`${fileList[i].name}`)
                    if (fileExtUploadFilelist.indexOf(fileExt) === -1) {
                        appAlertMessageAction.openAlertMessage({
                            title: translate.get('ALERT.TITLE.WARNING'),
                            message: translate.get('LABEL.UPLOAD_NOT_EXCEED_30_MB'),
                        })

                        lstFile = []
                        lstDisplayName = []

                        break
                    }
                    lstFile.push(file)
                    lstDisplayName.push(file.name)
                }
            }
        }

        // --- ############################################################################## --- //

        const isDuplicateFile = checkDuplicateFile(lstFile)
        if (isDuplicateFile) {
            appAlertMessageAction.openAlertMessage({
                title: translate.get('ALERT.TITLE.WARNING'),
                message: translate.get('ALERT.CONTENT.UPLOAD_FILE_DUPLICATED'),
            })
        } else {
            const uploadRes = await callUploadFile(lstFile, mainTopicID)
            let myNewListFile: NewReturnListFileType[]
            myNewListFile = uploadRes.map((itm: UploadResponseType) => {
                return {
                    documentTopicID: mainTopicID,
                    fileToken: `${itm.fileToken}`,
                    filename: itm.filename,
                    //fileID: `${itm.fileToken}`,
                    path: itm.path,
                    isNewFile: true,
                }
            })

            //@ts-ignore
            setLstFileUpload([...lstFileUpload, ...myNewListFile]) // fordisplay
            //@ts-ignore
            let newFileList = returnData ? [...returnData?.fileList, ...myNewListFile] : [...myNewListFile]
            const newReturnData = {
                ...returnData,
                //@ts-ignore
                fileList: newFileList,
                documentTopicID: mainTopicID,
            }
            setReturnData(newReturnData)
            field.input.onChange(newReturnData)
            refElementTextField.current.value = lstDisplayName.join(', ')
        }
    }

    const callUploadFile = async (fileList: Array<MyFileType>, mainTopicID: number) => {
        var formData = new FormData()
        fileList.forEach((file: MyFileType, index: number) => {
            formData.append('files', file)
        })

        const res = await callWebService.uploadFileTemp(formData)
        return res.result
    }
    const handleClickClear = async (file: any) => {
        const deleteTempParam: requestWebServiceTypes.DeleteTempType = {
            fileToken: [file.fileToken],
        }
        const { status } = await callWebService.deleteTempFile(deleteTempParam)

        if (status === 1) {
            let currentValue: ReturnDataType = field.input.value as unknown as ReturnDataType
            let newLstFile: any = []
            if (file.replaceFileID) {
                let oldReplaceFile = lstFileChange?.filter(
                    (itm: any) => itm.fileID === file.replaceFileID || itm.replaceFileID === file.replaceFileID,
                )
                newLstFile = currentValue.fileList?.map((itm) => {
                    if (itm.replaceFileID === file.replaceFileID) {
                        return oldReplaceFile?.[0] || itm
                    } else {
                        return itm
                    }
                })
            } else if (file.isNewFile) {
                newLstFile = currentValue.fileList?.filter((itm) => itm.fileToken !== file.fileToken)
            } else {
                newLstFile = currentValue.fileList?.filter((itm) => itm.fileID !== file.fileID)
            }

            let newReturnData = { ...returnData, fileList: newLstFile }
            if (newLstFile.length === 0) {
                newReturnData = { ...returnData, fileList: newLstFile, subTopicPageList: [] } //กรณีลบไฟล์ออกหมดจะให้เคลียร์ค่า page ออกด้วย
            } else {
                newReturnData = { ...returnData, fileList: newLstFile }
            }
            setReturnData(newReturnData)
            field.input.onChange(newReturnData)
            setLstFileUpload(newLstFile)
            refElementTextField.current.value = newLstFile?.join(', ')
        } else {
            alert(translate.get('MESSAGE.M010'))
        }
    }

    const handleChangeInputPage = (page: string, subTopicID: number, mainTopicID?: number | null) => {
        let obj = { page: page, subtopicID: subTopicID }

        // --- Logic --- //
        //1. delete old value
        //2. push new value
        let newReturnData = null
        if (returnData?.subTopicPageList && returnData?.subTopicPageList?.length > 0) {
            const deletedOldSubTopicList = returnData.subTopicPageList.filter((itm: SubTopicPageList) => {
                //delete old value
                return itm.mainTopicID !== mainTopicID || itm.subTopicID !== subTopicID
            })

            let newSubTopicPageList = [...deletedOldSubTopicList, { ...obj }].filter(
                //@ts-ignore
                (itm) => itm.subtopicID !== subTopicID,
            ) //push new value
            newReturnData = { ...returnData, subTopicPageList: [...newSubTopicPageList, obj] }
            setReturnData(newReturnData)
            field.input.onChange(newReturnData)
        } else {
            newReturnData = { ...returnData, subTopicPageList: [obj] }
            setReturnData(newReturnData)
            field.input.onChange(newReturnData)
        }
    }

    const queryFileHistory = async (fileID: any, fileReplaceID: any) => {
        const { result } = await callWebService.getFileHistory(fileID, fileReplaceID)
        setLstFileHistory(result)
    }

    return {
        refElementUpload,
        refElementUploadEdit,
        refElementTextField,
        refDialog,
        refDialogDownload,
        field,
        lstFileUpload,
        lstFileHistory,
        onClickUpload: handleClickUpload,
        onClickChange: handleClickChange,
        onChangeUpload: handleChangeUpload,
        onChangeUploadEdit: handleChangeUploadEdit,
        onClickClear: handleClickClear,
        onChangeInputPage: handleChangeInputPage,
        queryFileHistory: queryFileHistory,
        resetLstFileUpload,
    }
}

export default useUploadFile
